import { fetchWithAuth } from '../utils/auth';
import { WS_API_ENDPOINTS } from '../config/api';

export type CashoutApplicationStatus = 'PENDING' | 'APPROVED' | 'REJECTED';
export type CashoutApplicationStatusFilter = 'ALL' | CashoutApplicationStatus;

export interface CashoutApplication {
  user_id: string;
  user_name?: string | null;
  status: CashoutApplicationStatus;
  applied_at?: string | null;
  reviewed_at?: string | null;
  reviewed_by?: string | null;
  note?: string | null;
}

export interface CashoutApplicationStats {
  total: number;
  pending: number;
  approved: number;
  rejected: number;
}

export interface ListCashoutApplicationsResponse {
  applications: CashoutApplication[];
  total_count: number;
  page: number;
  limit: number;
  total_pages: number;
  has_more: boolean;
  stats: CashoutApplicationStats;
}

export interface CashoutApplicationProfile {
  user_id: string;
  user_name?: string | null;
  name?: string | null;
  profile_image_url?: string | null;
  account_status?: string;
  email?: string | null;
  phone?: string | null;
  phone_iso?: string | null;
  location?: string | null;
  country_code?: string | null;
  region?: string | null;
  followers_count?: number;
  following_count?: number;
  total_likes?: number;
  trusted_score?: number;
  last_active_time?: number | string | null;
  ip_address?: string | null;
  registration_device?: string | null;
  registration_type?: string | null;
  is_dev_account?: boolean;
  is_email_verified: boolean;
  is_phone_verified: boolean;
  is_verified?: boolean;
  is_age_verified?: boolean;
  is_plus_verified: boolean;
  is_plus_subscribed?: boolean;
  is_manual_verification?: boolean;
  plus_subscription_expiration?: string | null;
  current_product_id?: string | null;
  revenuecat_original_expiration?: Record<string, any> | null;
  policy_violations_count?: number;
  policy_violations?: CashoutPolicyViolation[];
  created_at?: string | null;
  updated_at?: string | null;
}

export interface CashoutApplicationEligibility {
  account_verified: boolean;
  plus_subscription_active: boolean;
  invites: { required: number; completed: number };
  video_uploaded: boolean;
  streak: { required_days: number; current_days: number };
  no_policy_violations: boolean;
  payout_method_added: boolean;
  progress: { required: number; completed: number };
  is_eligible: boolean;
}

export interface CashoutTokenWallet {
  user_id?: string | null;
  user_name?: string | null;
  reward_tokens: number;
  purchased_tokens: number;
  gifted_tokens: number;
  total_tokens: number;
  is_active: boolean;
  last_transaction_date?: string | null;
  created_at?: string | null;
  updated_at?: string | null;
}

export interface CashoutCoinWallet {
  user_id?: string | null;
  user_name?: string | null;
  reward_coins: number;
  plus_reward_coins: number;
  purchased_coins: number;
  gifted_coins: number;
  tip_coins: number;
  referral_coins: number;
  total_coins: number;
  is_active: boolean;
  last_transaction_date?: string | null;
  created_at?: string | null;
  updated_at?: string | null;
}

export interface CashoutWalletTransaction {
  transaction_id: string;
  amount: number;
  token_type?: string | null;
  coin_type?: string | null;
  transaction_type?: string | null;
  transaction_source?: string | null;
  transaction_source_group?: string | null;
  transaction_status?: string | null;
  source_id?: string | null;
  title?: string | null;
  description?: string | null;
  balance?: Record<string, any> | null;
  metadata?: Record<string, any> | null;
  created_at?: string | null;
  updated_at?: string | null;
}

export interface CashoutPolicyViolation {
  violation_id?: string | null;
  code?: string | null;
  reason?: string | null;
  source?: string | null;
  moderator_user_id?: string | null;
  video_id?: string | null;
  created_at?: string | null;
  expires_at?: string | null;
}

export interface CashoutPayoutMethodSummary {
  method_id: string;
  type: string;
  label?: string | null;
  display_name: string;
  is_default: boolean;
  is_verified: boolean;
  is_active: boolean;
  verification_status: PayoutMethodVerificationStatus;
  verification_note?: string | null;
  verified_at?: string | null;
  bank_name?: string | null;
  account_holder_name?: string | null;
  account_number_last4?: string | null;
  currency_code?: string | null;
  bank_country?: string | null;
  bank_city?: string | null;
  receiver_country?: string | null;
  upi_id?: string | null;
  paypal_email?: string | null;
  card_last4?: string | null;
  card_brand?: string | null;
  created_at?: string | null;
  updated_at?: string | null;
}

export interface CashoutWithdrawalSummary {
  total_requests: number;
  total_tokens: number;
  total_gross_usd: number;
  total_net_usd: number;
  processing_count: number;
  completed_count: number;
  failed_count: number;
  cancelled_count: number;
  processing_tokens: number;
  completed_tokens: number;
  failed_tokens: number;
  cancelled_tokens: number;
  processing_net_usd: number;
  completed_net_usd: number;
  failed_net_usd: number;
  cancelled_net_usd: number;
  first_created_at?: string | null;
  last_created_at?: string | null;
  last_processed_at?: string | null;
}

export interface CashoutPlusSummary {
  account_verified: boolean;
  has_plus_access: boolean;
  has_paid_plus: boolean;
  is_trial_active: boolean;
  expiration_date?: string | null;
  product_id?: string | null;
  latest_subscription?: {
    event_type?: string | null;
    status?: string | null;
    product_id?: string | null;
    period_type?: string | null;
    provider?: string | null;
    store?: string | null;
    country_code?: string | null;
    purchased_at?: string | null;
    expires_at?: string | null;
    cancelled_at?: string | null;
    expired_at?: string | null;
    environment?: string | null;
    created_at?: string | null;
    updated_at?: string | null;
  } | null;
}

export interface CashoutApplicationDetail {
  application: CashoutApplication | null;
  profile: CashoutApplicationProfile | null;
  eligibility: CashoutApplicationEligibility;
  plus?: CashoutPlusSummary;
  wallets?: {
    tokens: CashoutTokenWallet;
    coins: CashoutCoinWallet;
  };
  payout_methods?: CashoutPayoutMethodSummary[];
  withdrawal_summary?: CashoutWithdrawalSummary;
  transactions?: {
    limit: number;
    tokens: CashoutWalletTransaction[];
    coins: CashoutWalletTransaction[];
    notable_tokens?: CashoutWalletTransaction[];
    notable_coins?: CashoutWalletTransaction[];
  };
}

export interface ListCashoutApplicationsParams {
  status?: CashoutApplicationStatusFilter;
  page?: number;
  limit?: number;
}

const parseJson = async (response: Response) => response.json().catch(() => null);
const unwrapData = <T>(json: any): T => (json?.data ?? json) as T;
const getErrorMessage = (json: any, fallback: string) => json?.message || json?.error || fallback;
const CASHOUT_API_BASE_URL = WS_API_ENDPOINTS.prod;

const request = async <T>(path: string, options: RequestInit = {}, fallbackError: string): Promise<T> => {
  const response = await fetchWithAuth(`${CASHOUT_API_BASE_URL}${path}`, options);
  const json = await parseJson(response);
  if (!response.ok) throw new Error(getErrorMessage(json, fallbackError));
  return unwrapData<T>(json);
};

export const listCashoutApplications = async (
  params: ListCashoutApplicationsParams = {},
): Promise<ListCashoutApplicationsResponse> => {
  const query = new URLSearchParams();
  if (params.status && params.status !== 'ALL') query.set('status', params.status);
  if (params.page !== undefined) query.set('page', String(params.page));
  if (params.limit !== undefined) query.set('limit', String(params.limit));

  const raw = await request<any>(
    `/admin/wallet/applications?${query.toString()}`,
    undefined,
    'Failed to load cashout applications',
  );

  return {
    applications: Array.isArray(raw?.applications) ? raw.applications : [],
    total_count: Number(raw?.total_count ?? 0),
    page: Number(raw?.page ?? params.page ?? 0),
    limit: Number(raw?.limit ?? params.limit ?? 20),
    total_pages: Number(raw?.total_pages ?? 0),
    has_more: Boolean(raw?.has_more),
    stats: raw?.stats ?? { total: 0, pending: 0, approved: 0, rejected: 0 },
  };
};

export const getCashoutApplicationDetail = async (userId: string): Promise<CashoutApplicationDetail> => {
  return request<CashoutApplicationDetail>(
    `/admin/wallet/applications/${encodeURIComponent(userId)}`,
    undefined,
    'Failed to load cashout application detail',
  );
};

export const reviewCashoutApplication = async (
  userId: string,
  status: 'APPROVED' | 'REJECTED' | 'PENDING',
  note?: string,
): Promise<{ user_id: string; status: string }> => {
  return request<any>(
    `/admin/wallet/applications/${encodeURIComponent(userId)}`,
    {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ status, ...(note ? { note } : {}) }),
    },
    'Failed to review cashout application',
  );
};

// ─── Withdrawal Requests ─────────────────────────────────────────────────────

export type WithdrawalRequestStatus = 'processing' | 'completed' | 'failed' | 'cancelled';
export type WithdrawalRequestStatusFilter = 'ALL' | WithdrawalRequestStatus;

export interface WithdrawalRequest {
  request_id: string;
  reference_id: string;
  user_id: string;
  user_name?: string | null;
  method_type: string;
  method_label?: string | null;
  tokens: number;
  gross_usd: number;
  platform_fee_usd: number;
  payout_fee_usd: number;
  net_usd: number;
  status: WithdrawalRequestStatus;
  failure_reason?: string | null;
  estimated_arrival_from?: string | null;
  estimated_arrival_to?: string | null;
  processed_at?: string | null;
  created_at?: string | null;
  updated_at?: string | null;
  payout_method_snapshot?: {
    type: string;
    label?: string;
    bank_name?: string;
    account_holder_name?: string;
    account_number_last4?: string;
    ifsc?: string;
    upi_id?: string;
  } | null;
}

export interface WithdrawalRequestStats {
  processing: number;
  completed: number;
  failed: number;
}

export interface ListWithdrawalRequestsResponse {
  requests: WithdrawalRequest[];
  total_count: number;
  page: number;
  limit: number;
  total_pages: number;
  has_more: boolean;
  stats: WithdrawalRequestStats;
}

export const listWithdrawalRequests = async (params: {
  status?: WithdrawalRequestStatusFilter;
  page?: number;
  limit?: number;
} = {}): Promise<ListWithdrawalRequestsResponse> => {
  const query = new URLSearchParams();
  if (params.status && params.status !== 'ALL') query.set('status', params.status);
  if (params.page !== undefined) query.set('page', String(params.page));
  if (params.limit !== undefined) query.set('limit', String(params.limit));

  const raw = await request<any>(
    `/admin/wallet/withdrawals?${query.toString()}`,
    undefined,
    'Failed to load withdrawal requests',
  );

  return {
    requests: Array.isArray(raw?.requests) ? raw.requests : [],
    total_count: Number(raw?.total_count ?? 0),
    page: Number(raw?.page ?? params.page ?? 0),
    limit: Number(raw?.limit ?? params.limit ?? 20),
    total_pages: Number(raw?.total_pages ?? 0),
    has_more: Boolean(raw?.has_more),
    stats: raw?.stats ?? { processing: 0, completed: 0, failed: 0 },
  };
};

export const getUserWithdrawalRequests = async (userId: string): Promise<WithdrawalRequest[]> => {
  const raw = await request<any>(
    `/admin/wallet/withdrawals/user/${encodeURIComponent(userId)}`,
    undefined,
    'Failed to load user withdrawal requests',
  );
  return Array.isArray(raw?.requests) ? raw.requests : [];
};

export const updateWithdrawalRequest = async (
  requestId: string,
  status: 'completed' | 'failed' | 'cancelled' | 'processing',
  failureReason?: string,
): Promise<{ request_id: string; status: string }> => {
  return request<any>(
    `/admin/wallet/withdrawals/${encodeURIComponent(requestId)}`,
    {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ status, ...(failureReason ? { failure_reason: failureReason } : {}) }),
    },
    'Failed to update withdrawal request',
  );
};

// ─── Payout Methods (Bank Account Verification) ──────────────────────────────

export type PayoutMethodVerificationStatus = 'PENDING' | 'APPROVED' | 'REJECTED';
export type PayoutMethodVerificationStatusFilter = 'ALL' | PayoutMethodVerificationStatus;

export interface PayoutMethod {
  method_id: string;
  user_id: string;
  user_name?: string | null;
  method_type: string;
  label?: string | null;
  display_name: string;
  bank_name?: string | null;
  account_holder_name?: string | null;
  account_number_last4?: string | null;
  currency_code?: string | null;
  swift_bic?: string | null;
  iban_last4?: string | null;
  ifsc_code?: string | null;
  routing_number?: string | null;
  sort_code?: string | null;
  bank_country?: string | null;
  bank_city?: string | null;
  verification_status: PayoutMethodVerificationStatus;
  verification_note?: string | null;
  verified_at?: string | null;
  is_default: boolean;
  is_active: boolean;
  created_at?: string | null;
  updated_at?: string | null;
}

export interface PayoutMethodStats {
  total: number;
  pending: number;
  approved: number;
  rejected: number;
}

export interface ListPayoutMethodsResponse {
  methods: PayoutMethod[];
  total_count: number;
  page: number;
  limit: number;
  total_pages: number;
  has_more: boolean;
  stats: PayoutMethodStats;
}

export const listPayoutMethods = async (params: {
  verification_status?: PayoutMethodVerificationStatusFilter;
  method_type?: string;
  page?: number;
  limit?: number;
} = {}): Promise<ListPayoutMethodsResponse> => {
  const query = new URLSearchParams();
  if (params.verification_status && params.verification_status !== 'ALL') query.set('verification_status', params.verification_status);
  if (params.method_type) query.set('method_type', params.method_type);
  if (params.page !== undefined) query.set('page', String(params.page));
  if (params.limit !== undefined) query.set('limit', String(params.limit));

  const raw = await request<any>(
    `/admin/wallet/payout-methods?${query.toString()}`,
    undefined,
    'Failed to load payout methods',
  );

  return {
    methods: Array.isArray(raw?.methods) ? raw.methods : [],
    total_count: Number(raw?.total_count ?? 0),
    page: Number(raw?.page ?? params.page ?? 0),
    limit: Number(raw?.limit ?? params.limit ?? 20),
    total_pages: Number(raw?.total_pages ?? 0),
    has_more: Boolean(raw?.has_more),
    stats: raw?.stats ?? { total: 0, pending: 0, approved: 0, rejected: 0 },
  };
};

export const reviewPayoutMethod = async (
  methodId: string,
  status: PayoutMethodVerificationStatus,
  note?: string,
): Promise<{ method_id: string; verification_status: string }> => {
  return request<any>(
    `/admin/wallet/payout-methods/${encodeURIComponent(methodId)}`,
    {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ status, ...(note ? { note } : {}) }),
    },
    'Failed to review payout method',
  );
};

// ─── Withdrawal Request Detail ────────────────────────────────────────────────

export interface WithdrawalRequestDetail extends Omit<WithdrawalRequest, 'payout_method_snapshot'> {
  payout_method_snapshot: PayoutMethodSnapshot | null;
  user_profile: {
    user_id: string;
    user_name?: string | null;
    name?: string | null;
    profile_image_url?: string | null;
    account_status?: string;
  } | null;
}

export interface PayoutMethodSnapshot {
  method_id?: string;
  type: string;
  label?: string | null;
  display_name?: string;
  bank_name?: string | null;
  account_holder_name?: string | null;
  account_number_last4?: string | null;
  currency_code?: string | null;
  swift_bic?: string | null;
  iban_last4?: string | null;
  ifsc_code?: string | null;
  routing_number?: string | null;
  sort_code?: string | null;
  bsb_code?: string | null;
  bank_country?: string | null;
  bank_city?: string | null;
  upi_id?: string | null;
  paypal_email?: string | null;
  card_last4?: string | null;
  card_brand?: string | null;
}

export const getWithdrawalRequestDetail = async (requestId: string): Promise<WithdrawalRequestDetail> => {
  return request<WithdrawalRequestDetail>(
    `/admin/wallet/withdrawals/${encodeURIComponent(requestId)}`,
    undefined,
    'Failed to load withdrawal request detail',
  );
};

// ─── Payout Method Detail ─────────────────────────────────────────────────────

export interface PayoutMethodDetail {
  method_id: string;
  user_id: string;
  user_name?: string | null;
  user_profile_image?: string | null;
  method_type: string;
  label?: string | null;
  display_name: string;
  // Bank
  bank_name?: string | null;
  account_holder_name?: string | null;
  account_number_last4?: string | null;
  currency_code?: string | null;
  swift_bic?: string | null;
  iban?: string | null;
  iban_last4?: string | null;
  ifsc_code?: string | null;
  routing_number?: string | null;
  sort_code?: string | null;
  bsb_code?: string | null;
  transit_number?: string | null;
  institution_number?: string | null;
  local_routing_code?: string | null;
  bank_street_address?: string | null;
  bank_country?: string | null;
  bank_city?: string | null;
  bank_postcode?: string | null;
  receiver_street_address?: string | null;
  receiver_country?: string | null;
  receiver_city?: string | null;
  receiver_postcode?: string | null;
  // Other types
  upi_id?: string | null;
  paypal_email?: string | null;
  card_last4?: string | null;
  card_brand?: string | null;
  card_expiry_month?: string | null;
  card_expiry_year?: string | null;
  billing_country?: string | null;
  // Status
  verification_status: PayoutMethodVerificationStatus;
  verification_note?: string | null;
  verified_at?: string | null;
  is_default: boolean;
  is_active: boolean;
  is_verified: boolean;
  created_at?: string | null;
  updated_at?: string | null;
}

export interface PayoutMethodDetailResponse {
  method: PayoutMethodDetail;
  user_profile: {
    user_id: string;
    user_name?: string | null;
    name?: string | null;
    profile_image_url?: string | null;
    account_status?: string;
  } | null;
  withdrawal_requests: WithdrawalRequest[];
}

export const getPayoutMethodDetail = async (methodId: string): Promise<PayoutMethodDetailResponse> => {
  return request<PayoutMethodDetailResponse>(
    `/admin/wallet/payout-methods/${encodeURIComponent(methodId)}`,
    undefined,
    'Failed to load payout method detail',
  );
};

// ─── Make user eligible ───────────────────────────────────────────────────────

export const adminMakeUserEligible = async (
  username: string,
): Promise<{ user_id: string; user_name: string; changes: string[] }> => {
  return request<any>(
    '/admin/wallet/make-eligible',
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ username }),
    },
    'Failed to make user eligible',
  );
};
